import React, { useCallback, useEffect, useMemo, useState } from "react"
import { View, StyleSheet, Text } from "react-native"
import { useFocusEffect } from "@react-navigation/native"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { theme } from "@common/theme/theme"
import FunnelStepHeader from "@common/components/funnel/FunnelStepHeader"
import FunnelStepLayout from "@common/components/funnel/FunnelStepLayout"
import { useFunnelTutorReveal } from "@common/components/funnel/useFunnelTutorReveal"
import SummerTutorsContent from "../summer/components/SummerTutorsContent"
import SummerEyebrow from "../summer/components/SummerEyebrow"
import {
    FUNNEL_TITLE_FONT_SIZE,
    FUNNEL_TITLE_LINE_HEIGHT,
} from "@common/components/funnel/FunnelQuestionScreen"
import SummerHelpBanner from "../summer/components/SummerHelpBanner"
import { elide } from "@common/utils/activationSuiteDisplay"
import { useStageFunnel } from "./context/StageFunnelContext"
import { useStageTrack } from "./hooks/useStageTrack"
import { useStageFunnelGuard } from "./hooks/useStageFunnelGuard"
import { useStageFunnelConfig } from "./hooks/useStageFunnelConfig"
import { stageFunnelService } from "./services/stageFunnelService"
import { getTemplateName } from "./constants/stageContent"
import FunnelTutorSearch from "@common/components/funnel/FunnelTutorSearch"
import FunnelFadeUp from "@common/components/funnel/FunnelFadeUp"

/**
 * Étape "aha moment tuteur humain" du funnel Stage — entre le programme
 * (stage/plan) et le checkout 1 € (stage/checkout). Réutilise le contenu
 * présentationnel de l'été (SummerTutorsContent) sur le matching stage :
 * POST /stage-funnel/{stateId}/matching-tutors (même shape de réponse).
 *
 * Étape NON bloquante : en cas d'erreur, de réponse vide ou de contexte pas
 * encore hydraté, repli gracieux (titre rassurant sans chiffre + bloc
 * garanties) et le CTA "Réserver la place" route TOUJOURS vers le checkout.
 * Jamais de spinner infini, jamais de cul-de-sac.
 */

const StageTutorsScreen = ({ navigation }) => {
    const { state } = useStageFunnel()
    const track = useStageTrack()
    // Refresh-safe / URL-partageable (voir useStageFunnelGuard).
    const { nextParams } = useStageFunnelGuard()

    const [loading, setLoading] = useState(true)
    const [matching, setMatching] = useState(null)
    const [failed, setFailed] = useState(false)
    // La recherche mise en scène tient l'écran jusqu'à ce que ses critères
    // soient cochés ET que le matching ait répondu — jamais moins, jamais un
    // spinner infini (elle rend la main en cas d'échec).
    const [searching, setSearching] = useState(true)
    // Vivier dans la scène, compte qui monte, cartes retenues : la même
    // révélation que le funnel Rentrée.
    const { showcase, shownCount, scene } = useFunnelTutorReveal({
        loading,
        matching,
        searching,
    })

    const firstname = state.studentFirstname || "votre enfant"
    // En-tête stage (nom du template + icône stage), comme les autres écrans du
    // funnel — surtout PAS « ☀️ Cours d'été » (défaut de SummerTutorsContent).
    // Nom du stage : catalogue en priorité (une reprise directe sur cet écran
    // n'a pas encore de templateName en contexte — le hook le synchronise).
    const { config } = useStageFunnelConfig(state.funnelStateId)
    const templateName = getTemplateName(config) || state.templateName
    const stageEyebrowLabel = `📚 ${templateName || "Stage"}`

    const funnelStateId = Number(state.funnelStateId)
    const hasValidStateId = Number.isInteger(funnelStateId) && funnelStateId > 0
    // Le matching lit le panier dans le body : matières + classe. Composition
    // v2 : les matières sont PAR session — le matching couvre leur UNION
    // (le tuteur doit maîtriser toutes les matières du stage).
    // Les matières de TOUTES les sessions composées. Surtout pas indexées par
    // `state.sessions` : en unité créneau ces clés sont des créneaux
    // (`2026-09-12T10:00`) alors que les matières se rangent par session
    // (`2026-09-12`) — la jointure était vide, et l'écran retombait toujours
    // sur le repli sans chiffre ni carte. Le configurateur a déjà élagué les
    // matières aux sessions cochées.
    const subjects = [
        ...new Set(Object.values(state.sessionSubjects || {}).flat()),
    ]
    const hasMatchingPayload =
        hasValidStateId && subjects.length > 0 && !!state.studentClass

    useEffect(() => {
        track("stage_funnel_tutors_viewed")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Charge le matching au focus (une seule passe par hydratation).
    useFocusEffect(
        useCallback(() => {
            // Contexte froid : la garde redirige vers stage/resume.
            if (!hasValidStateId) return
            // Déjà chargé (succès ou repli) : pas de re-fetch — le panier est
            // figé à ce stade du funnel.
            if (matching || failed) return
            // Panier incomplet (cas limite) → repli direct, pas d'appel voué au 422.
            if (!hasMatchingPayload) {
                setFailed(true)
                setLoading(false)
                return
            }

            let cancelled = false
            setLoading(true)
            setFailed(false)
            stageFunnelService
                .getStageMatchingTutors(
                    { stateId: funnelStateId },
                    {
                        subjects,
                        studentClass: state.studentClass,
                        // Composition choisie : enrichit la signature de tirage
                        // déterministe côté backend (mêmes params → mêmes cartes).
                        slotKey: state.slotKey,
                    }
                )
                .then((res) => {
                    if (cancelled) return
                    const payload = res?.data?.data ?? res?.data ?? null
                    // Réponse vide ou sans compte exploitable → repli gracieux.
                    if (!payload || !(payload.count > 0)) {
                        setFailed(true)
                        return
                    }
                    setMatching(payload)
                })
                .catch(() => {
                    if (cancelled) return
                    // Erreur (réseau / 4xx / 5xx) : on NE bloque PAS le funnel.
                    setFailed(true)
                })
                .finally(() => {
                    if (!cancelled) setLoading(false)
                })
            return () => {
                cancelled = true
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [hasValidStateId, hasMatchingPayload])
    )

    // Les critères, avec les mots de l'élève : son niveau et son profil (le
    // mot reste vague à dessein — il couvre les spécificités exprimées comme
    // celles qui ne le sont pas), ses matières, ses créneaux. Par discipline,
    // pas par niveau visé — c'est la matière qui fait le masteur.
    const criteria = useMemo(() => {
        const lines = [
            state.studentClass
                ? `Adaptés au niveau ${state.studentClass} et au profil ${elide(firstname)}`
                : `Adaptés au niveau et au profil ${elide(firstname)}`,
        ]
        const names = subjects
            .map((id) => state.subjectNames?.[String(id)])
            .filter(Boolean)
        lines.push(
            names.length
                ? `Spécialistes ${names.length > 1 ? "de " + names.slice(0, -1).join(", ") + " et " + names[names.length - 1] : "de " + names[0]}`
                : "Spécialistes de ses matières"
        )
        const slots = (state.sessions || [])
            .map((key) => {
                if (!/T\d{2}:\d{2}$/.test(key)) return null
                const d = new Date(key)
                if (Number.isNaN(d.getTime())) return null
                const day = d.toLocaleDateString("fr-FR", { weekday: "long" })
                return `${day.charAt(0).toUpperCase()}${day.slice(1)} ${d.getHours()}h`
            })
            .filter(Boolean)
        lines.push(
            slots.length
                ? `Disponibles ${slots.join(" · ")}`
                : "Disponibles sur vos créneaux"
        )
        return lines
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        state.studentClass,
        state.sessions,
        state.subjectNames,
        subjects.join(),
        firstname,
    ])
    return (
        <FunnelStepLayout
            scene={scene}
            contentContainerStyle={styles.content}
            header={
                <FunnelStepHeader
                    step={5}
                    total={6}
                    onBack={() => navigation.goBack()}
                />
            }
            footer={
                <View style={styles.footer}>
                    <FilledButtonM
                        label="Réserver la place"
                        onPress={() => {
                            track("stage_funnel_tutors_completed")
                            navigation.navigate("stage/checkout", nextParams)
                        }}
                        // Désactivé tant que le matching n'a pas chargé : laisse
                        // les écritures amont atterrir avant d'ouvrir le checkout.
                        disabled={loading || searching}
                        color={
                            loading || searching
                                ? theme.colors.lightMore
                                : theme.colors.primary
                        }
                        style={{ width: "100%", marginBottom: 0 }}
                    />
                </View>
            }
        >
            {searching ? (
                <>
                    {/* L'écran garde son en-tête pendant la recherche : les
                        étapes qui se cochent ne flottent pas dans le vide. */}
                    <SummerEyebrow label={stageEyebrowLabel} />
                    <Text style={styles.title}>
                        On cherche les masteurs {elide(firstname)}
                    </Text>
                    <FunnelTutorSearch
                        criteria={criteria}
                        found={!loading}
                        onDone={() => setSearching(false)}
                    />
                </>
            ) : (
                <FunnelFadeUp>
                    <SummerTutorsContent
                        firstname={firstname}
                        cardAvatarSize={72}
                        stackAvatarSize={44}
                        // La recherche vient d'être racontée critère par
                        // critère : redire « choisis pour leur maîtrise… »
                        // n'apporte rien. Seule exception, l'élève déjà suivi :
                        // le serveur dit si l'offre ira d'abord à son masteur
                        // (le ciblage de la cohorte), et on le dit sans le
                        // promettre — le stage se tient sur d'autres créneaux.
                        subtitle={
                            matching?.regular_tutor_first
                                ? "Son masteur habituel est sollicité en priorité, sans garantie qu'il soit libre sur ces créneaux."
                                : null
                        }
                        // « Attribué avant le 1ᵉʳ cours » va de soi ; ce que le
                        // parent gagne, c'est de savoir ce qui s'est passé.
                        thirdGuarantee={{
                            icon: "clipboard-check",
                            label: "Un compte-rendu après chaque séance",
                        }}
                        count={shownCount}
                        total={matching?.total}
                        tutors={showcase}
                        moreAvatars={matching?.more_avatars}
                        failed={failed}
                        eyebrowLabel={stageEyebrowLabel}
                    />
                </FunnelFadeUp>
            )}
            <SummerHelpBanner
                track={track}
                step={5}
                trackingEvent="stage_funnel_help_clicked"
            />
        </FunnelStepLayout>
    )
}

const styles = StyleSheet.create({
    content: { paddingBottom: 24 },
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: FUNNEL_TITLE_FONT_SIZE,
        lineHeight: FUNNEL_TITLE_LINE_HEIGHT,
        color: theme.colors.primaryDark || theme.colors.primary,
        marginBottom: 14,
    },
    footer: {
        paddingVertical: 14,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
})

export default StageTutorsScreen
